import React, { useState, useEffect } from 'react';
import {
  Search,
  Copy,
  ExternalLink,
  Wallet,
  TrendingUp,
  Download,
  Eye,
  ArrowUpRight,
  ArrowDownLeft,
  Hash
} from 'lucide-react';

interface Address {
  address: string;
  balance: number;
  transactionCount: number;
  firstSeen: string;
  lastActivity: string;
  type: 'wallet' | 'contract' | 'validator';
  label?: string;
  tokens: {
    symbol: string;
    balance: number;
    value: number;
  }[];
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: number;
  timestamp: string;
  status: 'success' | 'failed' | 'pending';
  gasUsed: number;
  gasPrice: number;
}

const AddressesPage: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [addressTransactions, setAddressTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const addressesPerPage = 20;

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      
      // 실제 API 호출 - voltron_users에서 주소 목록 가져오기
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      
      try {
        // 회원 주소 목록 조회
        const response = await fetch(`${apiBaseUrl}/admin/users?limit=1000&page=1`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.users) {
            const addressesList: Address[] = data.users
              .filter((user: any) => user.walletAddress && user.walletAddress.startsWith('0x'))
              .map((user: any, index: number) => ({
                address: user.walletAddress,
                balance: parseFloat(user.vttnBalance || 0),
                transactionCount: 0, // 추후 계산
                firstSeen: user.createdAt || new Date().toISOString(),
                lastActivity: user.updatedAt || user.createdAt || new Date().toISOString(),
                type: 'wallet' as any,
                label: user.nickname || undefined,
                tokens: [
                  { symbol: 'VTTN', balance: parseFloat(user.vttnBalance || 0), value: parseFloat(user.vttnBalance || 0) * 0.5 },
                  { symbol: 'USDT', balance: parseFloat(user.usdtBalance || 0), value: parseFloat(user.usdtBalance || 0) }
                ]
              }));
            
            setAddresses(addressesList);
            setLoading(false);
            return;
          }
        }
      } catch (apiError) {
        console.error('API 호출 실패:', apiError);
      }
      
      // API 실패 시 빈 배열
      setAddresses([]);
      setLoading(false);
    } catch (error) {
      console.error('주소 목록 로딩 실패:', error);
      setLoading(false);
    }
  };

  const loadAddressTransactions = async (address: string) => {
    try {
      // 실제 API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/explorer/addresses/${address}/transactions?limit=20`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.transactions) {
          const transactions: Transaction[] = data.transactions.map((tx: any) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: parseFloat(tx.value || '0'),
            timestamp: typeof tx.timestamp === 'number' 
              ? new Date(tx.timestamp * 1000).toISOString() 
              : new Date(tx.timestamp).toISOString(),
            status: tx.status || 'success',
            gasUsed: parseInt(tx.gasUsed || '0'),
            gasPrice: parseFloat(tx.gasPrice || '0') / 1e9
          }));
          
          setAddressTransactions(transactions);
          return;
        }
      }
      
      // API 실패 시 빈 배열
      setAddressTransactions([]);
    } catch (error) {
      console.error('주소 트랜잭션 로딩 실패:', error);
      setAddressTransactions([]);
    }
  };

  const handleAddressClick = (address: Address) => {
    setSelectedAddress(address);
    loadAddressTransactions(address.address);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사되었습니다.');
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'wallet': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'contract': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'validator': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'wallet': return <Wallet className="w-4 h-4" />;
      case 'contract': return <Hash className="w-4 h-4" />;
      case 'validator': return <TrendingUp className="w-4 h-4" />;
      default: return <Wallet className="w-4 h-4" />;
    }
  };

  const filteredAddresses = addresses.filter(address => {
    const matchesSearch = address.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (address.label && address.label.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = typeFilter === 'all' || address.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredAddresses.length / addressesPerPage);
  const paginatedAddresses = filteredAddresses.slice(
    (currentPage - 1) * addressesPerPage,
    currentPage * addressesPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">주소 탐색기</h1>
            <p className="text-gray-600 mt-1">볼트론 네트워크의 모든 주소를 탐색하고 분석하세요</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="주소 또는 라벨 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">모든 타입</option>
            <option value="wallet">지갑</option>
            <option value="contract">컨트랙트</option>
            <option value="validator">검증자</option>
          </select>
        </div>
      </div>

      {/* 주소 목록 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>주소</th>
                <th>타입</th>
                <th>잔액</th>
                <th>트랜잭션</th>
                <th>최근 활동</th>
                <th>액션</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAddresses.map((address) => (
                <tr key={address.address} className="hover:bg-gray-50">
                  <td>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        {getTypeIcon(address.type)}
                      </div>
                      <div>
                        <button
                          onClick={() => window.open(`/address/${address.address}`, '_blank')}
                          className="font-medium text-blue-600 hover:text-blue-800"
                        >
                          {formatAddress(address.address)}
                        </button>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-500 font-mono">
                            {address.address.slice(0, 10)}...{address.address.slice(-8)}
                          </span>
                          <button
                            onClick={() => copyToClipboard(address.address)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        {address.label && (
                          <p className="text-sm text-gray-500 mt-1">{address.label}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${getTypeColor(address.type)}`}>
                      <span className="capitalize">{address.type}</span>
                    </span>
                  </td>
                  <td>
                    <div>
                      <p className="font-medium text-gray-900">{address.balance.toFixed(2)} VTTN</p>
                      <p className="text-sm text-gray-500">${(address.balance * 0.125).toFixed(2)}</p>
                    </div>
                  </td>
                  <td>
                    <span className="font-medium text-gray-900">{address.transactionCount.toLocaleString()}</span>
                  </td>
                  <td>
                    <span className="text-sm text-gray-600">{formatDate(address.lastActivity)}</span>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleAddressClick(address)}
                        className="text-blue-600 hover:text-blue-800"
                        title="상세 보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(`/address/${address.address}`, '_blank')}
                        className="text-gray-400 hover:text-gray-600"
                        title="새 탭에서 열기"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {filteredAddresses.length}개 주소 중 {(currentPage - 1) * addressesPerPage + 1}-{Math.min(currentPage * addressesPerPage, filteredAddresses.length)}개 표시
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="px-3 py-1 text-sm font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        </div>
      </div>

      {/* 주소 상세 모달 */}
      {selectedAddress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">주소 상세 정보</h2>
              <button
                onClick={() => setSelectedAddress(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">기본 정보</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">주소:</span>
                      <span className="font-mono">{selectedAddress.address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">타입:</span>
                      <span className="capitalize">{selectedAddress.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">첫 등장:</span>
                      <span>{formatDate(selectedAddress.firstSeen)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">최근 활동:</span>
                      <span>{formatDate(selectedAddress.lastActivity)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">토큰 보유량</h3>
                  <div className="space-y-2">
                    {selectedAddress.tokens.map((token, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="font-medium">{token.symbol}</span>
                        <div className="text-right">
                          <p className="font-medium">{token.balance.toFixed(2)}</p>
                          <p className="text-sm text-gray-500">${token.value.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 최근 트랜잭션 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">최근 트랜잭션</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">해시</th>
                        <th className="px-4 py-2 text-left">방향</th>
                        <th className="px-4 py-2 text-left">금액</th>
                        <th className="px-4 py-2 text-left">시간</th>
                        <th className="px-4 py-2 text-left">상태</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {addressTransactions.slice(0, 10).map((tx) => (
                        <tr key={tx.hash}>
                          <td className="px-4 py-2 font-mono">{formatAddress(tx.hash)}</td>
                          <td className="px-4 py-2">
                            {tx.from === selectedAddress.address ? (
                              <div className="flex items-center space-x-1 text-red-600">
                                <ArrowUpRight className="w-3 h-3" />
                                <span>송금</span>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1 text-green-600">
                                <ArrowDownLeft className="w-3 h-3" />
                                <span>수신</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 font-medium">{tx.value.toFixed(4)} VTTN</td>
                          <td className="px-4 py-2">{formatDate(tx.timestamp)}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              tx.status === 'success' ? 'bg-green-100 text-green-800' :
                              tx.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {tx.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressesPage;